import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Users, Shield, Church, Heart } from "lucide-react";

export function MembershipModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-[#F9F7F2]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#2D2A26] font-['Cinzel'] flex items-center gap-2">
            <Church className="w-6 h-6 text-[var(--liturgical-primary)]" />
            Becoming a Parish Member
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 text-[#2D2A26] font-['Nunito']">
          <p className="text-base leading-relaxed">
            Parish is a community built on trust. To protect the integrity of our community and ensure a safe space for fellowship, certain features are available only to verified members of the Catholic Church.
          </p>
          
          <div className="bg-white rounded-lg p-4 border border-[#E5E0D5]">
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[var(--liturgical-primary)]" />
              How it works
            </h3>
            <p className="text-sm text-[#6B665E]">
              To join Parish, you'll need an invitation from a current member of your parish. This helps us confirm your connection to the Catholic community and links you to your local parish family.
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-[#E5E0D5]">
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <Heart className="w-5 h-5 text-[var(--liturgical-primary)]" />
              Why we do this
            </h3>
            <p className="text-sm text-[#6B665E]">
              By connecting members through their parishes, we foster a community rooted in shared faith and the <em>sensus fidelium</em>—the sense of the faithful. This creates a trusted environment where Catholics can connect, support one another, and grow together in faith.
            </p>
          </div>
          
          <div className="bg-[var(--liturgical-primary)]/10 rounded-lg p-4 border border-[var(--liturgical-primary)]/20">
            <p className="text-sm flex items-start gap-2">
              <Users className="w-5 h-5 text-[var(--liturgical-primary)] flex-shrink-0 mt-0.5" />
              <span>
                If you're Catholic and would like to join, reach out to a fellow parishioner who's already a member, or ask your parish office for more information.
              </span>
            </p>
          </div>
        </div>
        
        <div className="flex justify-end pt-2">
          <Button 
            onClick={() => onOpenChange(false)}
            className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
          >
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MembershipCTA({ context = "general", className = "" }) {
  const [modalOpen, setModalOpen] = React.useState(false);
  
  const messages = {
    marketplace: "Learn how to become a member to view this content.",
    directory: "Learn how to become a member to view this content.",
    parish: "Learn how to become a member to more fully interact with Parish.",
    vatican: "Learn how to become a member to engage with Vatican News posts.",
    general: "Learn how to become a member to more fully interact with Parish."
  };
  
  const message = messages[context] || messages.general;
  
  return (
    <>
      <button
        onClick={() => setModalOpen(true)}
        className={`text-[var(--liturgical-primary)] hover:underline font-['Nunito'] text-sm font-medium cursor-pointer ${className}`}
        data-testid="membership-cta"
      >
        {message}
      </button>
      <MembershipModal open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
}

export function MembershipBanner({ context = "general", className = "" }) {
  const [modalOpen, setModalOpen] = React.useState(false);
  
  const messages = {
    marketplace: "Learn how to become a member to view this content.",
    directory: "Learn how to become a member to view this content.",
    parish: "Learn how to become a member to more fully interact with Parish.",
    vatican: "Learn how to become a member to engage with Vatican News posts.",
    general: "Learn how to become a member to more fully interact with Parish."
  };
  
  const message = messages[context] || messages.general;
  
  return (
    <>
      <div 
        className={`bg-[var(--liturgical-primary)]/10 border border-[var(--liturgical-primary)]/20 rounded-lg p-4 ${className}`}
        data-testid="membership-banner"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--liturgical-primary)]/20 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-[var(--liturgical-primary)]" />
            </div>
            <p className="text-sm text-[#2D2A26] font-['Nunito']">
              {message}
            </p>
          </div>
          <Button
            onClick={() => setModalOpen(true)}
            variant="outline"
            size="sm"
            className="flex-shrink-0 border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10 font-['Nunito']"
            data-testid="membership-learn-more-btn"
          >
            Learn More
          </Button>
        </div>
      </div>
      <MembershipModal open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
}

export default MembershipModal;

